import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useLocation } from 'react-router-dom';
import { ChatMessage, chatService } from '../services/chatService';
import { useChatSettingsStore } from '@/store/chatSettingsStore';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dark, ghcolors } from 'react-syntax-highlighter/dist/esm/styles/prism';
import FieldPanel from '../components/chat/FieldPanel';
import SettingsBar from '../components/chat/SettingsBar';
import { ThinkingPanel } from '../components/chat/ThinkingPanel';
import styles from '../styles/pages/TextChatPage.module.css';
import { ThemeContext } from '../contexts/ThemeContext';
import { FiClipboard, FiCheck } from 'react-icons/fi';
import { parseThinkingFromText } from '../utils/thinking';

// Inline edit component for message bubbles
const InlineEditButton: React.FC<{
  messageId: string;
  initial: string;
  onSave: (text: string) => void;
}> = ({ messageId, initial, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(initial);

  useEffect(() => {
    setText(initial);
  }, [initial]);

  if (!isEditing) {
    return (
      <button
        className={styles.actionButton}
        onClick={() => setIsEditing(true)}
      >
        <img alt="Edit" src="/icons/chat/edit.svg" />
      </button>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <textarea
        className={styles.editTextarea}
        value={text}
        onChange={e => setText(e.target.value)}
      />
      <div className={styles.editActions}>
        <button
          className={styles.saveButton}
          onClick={() => {
            onSave(text);
            setIsEditing(false);
          }}
        >
          Save
        </button>
        <button
          className={styles.cancelButton}
          onClick={() => {
            setText(initial);
            setIsEditing(false);
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const TextChatPage: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsAnchor, setSettingsAnchor] = useState<{
    right?: number;
    bottom?: number;
  }>({});
  const [notice, setNotice] = useState<string | null>(null);
  const selectedModelId = useChatSettingsStore(s => s.selectedModelId);
  const temperature = useChatSettingsStore(s => s.temperature);
  const topP = useChatSettingsStore(s => s.topP);
  const topK = useChatSettingsStore(s => s.topK);
  const contextSize = useChatSettingsStore(s => s.contextSize);
  const systemPrompt = useChatSettingsStore(s => s.systemPrompt);
  const location = useLocation();

  // Get chatId and namespace from URL parameters
  const [chatId] = useState(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlChatId = searchParams.get('chatId');
    return urlChatId || chatService.generateChatId();
  });

  const [namespace] = useState(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('namespace') || 'frontend';
  });
  const setSelectedModelId = useChatSettingsStore(s => s.setSelectedModelId);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);
  const { isDark } = useContext(ThemeContext);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Apply selected model from navigation state when arriving from models gallery
  useEffect(() => {
    const state = location.state as { selectedModel?: string } | null;
    if (state?.selectedModel) {
      setSelectedModelId(state.selectedModel);
    }
  }, [location.state, setSelectedModelId]);

  // Load chat history on mount
  useEffect(() => {
    const load = async () => {
      const history = await chatService.loadChatHistory(namespace, chatId);
      setMessages(history);
    };
    load();
  }, [chatId, namespace]);

  // Save chat history when not streaming to avoid saving incorrect IDs before inference-id
  useEffect(() => {
    if (messages.length > 0 && !isStreaming) {
      chatService.saveChatHistory(namespace, chatId, messages).catch(() => {});
    }
  }, [messages, chatId, namespace, isStreaming]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    setNotice(null);
    const userMessage: ChatMessage = {
      id: chatService.generateMessageId(),
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setIsStreaming(true);

    let assistantMessage: ChatMessage | null = null;

    try {
      const apiModelName =
        await chatService.resolveApiModelName(selectedModelId);

      assistantMessage = {
        id: chatService.generateMessageId(),
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        model: { id: apiModelName },
      };

      setMessages(prev => [...prev, assistantMessage]);
      const allMessages: ChatMessage[] = [
        ...(systemPrompt
          ? ([{ role: 'system', content: systemPrompt }] as ChatMessage[])
          : []),
        ...messages,
        userMessage,
      ];

      console.log('🚀 Sending chat request');
      const stream = chatService.sendStreamingChatCompletion({
        model: apiModelName,
        messages: allMessages.map(m => ({ role: m.role, content: m.content })),
        stream: true,
        temperature,
        top_p: topP,
        top_k: topK,
        max_tokens: contextSize,
      });

      let fullContent = '';
      let updateQueue: string[] = [];
      let isUpdating = false;

      // Smooth streaming update function
      const updateUI = () => {
        if (updateQueue.length > 0 && !isUpdating) {
          isUpdating = true;
          requestAnimationFrame(() => {
            const newContent = updateQueue.join('');
            updateQueue = [];
            fullContent += newContent;

            setMessages(prev =>
              prev.map(msg =>
                assistantMessage && msg.id === assistantMessage.id
                  ? { ...msg, content: fullContent }
                  : msg
              )
            );

            // Auto-scroll during streaming for better UX
            scrollToBottom();
            isUpdating = false;
          });
        }
      };

      // Track and overwrite assistant message id with backend-generated id when available
      let assistantIdSet = false;

      for await (const chunk of stream) {
        // If backend emits an id in the chunk, adopt it for the assistant message
        if (!assistantIdSet && chunk?.id && assistantMessage) {
          assistantIdSet = true;
          const newId = chunk.id;
          const oldId = assistantMessage.id;
          if (newId && newId !== oldId) {
            assistantMessage = { ...assistantMessage, id: newId };
            setMessages(prev =>
              prev.map(msg => (msg.id === oldId ? { ...msg, id: newId } : msg))
            );
          }
        }
        const delta = chunk.choices[0]?.delta;
        if (delta) {
          const rc =
            (delta as any).reasoning_content ||
            (delta as any).thinking ||
            (delta as any).reasoning;
          if (typeof rc === 'string' && rc.length > 0) {
            updateQueue.push(`<think>${rc}</think>`);
          }
          if (typeof delta.content === 'string' && delta.content.length > 0) {
            updateQueue.push(delta.content);
          }
          if (updateQueue.length > 0) updateUI();
        }
        if (chunk.choices[0]?.finish_reason) {
          break;
        }
      }

      // Flush any remaining content
      if (updateQueue.length > 0) {
        updateUI();
      }
    } catch (e: any) {
      let friendly = 'Error fetching response';
      if (e?.code === 'INSUFFICIENT_BALANCE') {
        friendly =
          'Insufficient balance. Please top up your account to continue.';
      } else if (e?.code === 'MODEL_NOT_FOUND') {
        friendly =
          'Selected model is unavailable. Please pick another model in settings.';
      } else if (e?.message?.includes('MetaMask wallet address is required')) {
        friendly =
          'Please connect your MetaMask wallet to continue. Click the wallet connection button in the top bar.';
      }
      // Show transient error bubble instead of assistant reply and do NOT persist it in history
      setNotice(friendly);
      if (assistantMessage) {
        setMessages(prev => prev.filter(msg => msg.id !== assistantMessage.id));
      }
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const [isCopied, setIsCopied] = useState(false);

  const MarkdownBody: React.FC<{ text: string }> = ({ text }) => (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        code(props) {
          const { children, className } = props;
          const match = /language-(\w+)/.exec(className || '');
          const content = String(children).replace(/\n$/, '');
          const syntaxTheme = (isDark ? dark : ghcolors) as unknown as {
            [key: string]: React.CSSProperties;
          };

          const handleCopy = () => {
            navigator.clipboard.writeText(content);

            setIsCopied(true);

            setTimeout(() => {
              setIsCopied(false);
            }, 1000);
          };

          return match ? (
            <div className="syntax-highlighter-container">
              <SyntaxHighlighter
                children={content}
                language={match[1]}
                PreTag="div"
                style={syntaxTheme}
                customStyle={{
                  background: 'var(--surface-surface_1)',
                  borderRadius: '28px',
                  padding: '1.75em',
                  border: null,
                  boxShadow: null,
                  textShadow: null,
                  lineHeight: null,
                  fontFamily: null,
                  fontSize: '1em',
                }}
              />
              <span className="copy-icon" onClick={handleCopy}>
                {isCopied ? (
                  <FiCheck style={{ color: 'var(--success-color)' }} />
                ) : (
                  <FiClipboard style={{ color: 'var(--text-med_em)' }} />
                )}
              </span>
            </div>
          ) : (
            <code className={className} {...props}>
              {children}
            </code>
          );
        },
      }}
    >
      {text}
    </ReactMarkdown>
  );

  return (
    <div className={styles.textChatPage}>
      {/* Bottom underlay to hide content behind input when scrolling */}
      <div className={styles.inputUnderlay} />
      {/* Error / notice will render within the messages list below */}

      {/* Chat Messages Area */}
      <div className={styles.chatContainer}>
        <div className={styles.messagesArea}>
          {messages.map((message, idx) => (
            <div key={message.id} className={styles.messageGroup}>
              {message.role === 'user' ? (
                <div className={styles.userMessage}>
                  <div className={styles.messageContent}>{message.content}</div>
                  <div className={styles.messageActions}>
                    <button
                      className={styles.actionButton}
                      onClick={() =>
                        navigator.clipboard.writeText(message.content)
                      }
                    >
                      <img alt="Copy" src="/icons/chat/copy.svg" />
                    </button>
                    <InlineEditButton
                      messageId={message.id}
                      initial={message.content}
                      onSave={newText => {
                        setMessages(prev =>
                          prev.map(m =>
                            m.id === message.id ? { ...m, content: newText } : m
                          )
                        );
                        chatService
                          .saveChatHistory(
                            namespace,
                            chatId,
                            messages.map(m =>
                              m.id === message.id
                                ? { ...m, content: newText }
                                : m
                            )
                          )
                          .catch(() => {});
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className={styles.aiMessage}>
                  <div className={styles.aiContent}>
                    {(() => {
                      const modelForParse =
                        typeof (message as any)?.model === 'string'
                          ? ((message as any).model as string)
                          : ((message as any)?.model?.id ||
                            (message as any)?.model?.name ||
                            '') as string;
                      const parsed = parseThinkingFromText(message.content, {
                        model: modelForParse,
                      });
                      return (
                        <>
                          <ThinkingPanel
                            blocks={parsed.thinking}
                            isStreaming={
                              isStreaming &&
                              idx === messages.length - 1 &&
                              message.role === 'assistant'
                            }
                            renderBlock={text => <MarkdownBody text={text} />}
                          />
                          <MarkdownBody text={parsed.content} />
                        </>
                      );
                    })()}
                    {isStreaming &&
                      idx === messages.length - 1 &&
                      message.role === 'assistant' && (
                        <span className={styles.typingIndicator}>
                          <span className={styles.typingDot}></span>
                          <span className={styles.typingDot}></span>
                          <span className={styles.typingDot}></span>
                        </span>
                      )}
                  </div>
                  <div className={styles.aiActions}>
                    <div className={styles.actionGroup}>
                      <button
                        className={styles.actionButton}
                        onClick={() =>
                          navigator.clipboard.writeText(message.content)
                        }
                      >
                        <img alt="Copy" src="/icons/chat/copy.svg" />
                      </button>
                      <InlineEditButton
                        messageId={message.id}
                        initial={message.content}
                        onSave={newText => {
                          setMessages(prev =>
                            prev.map(m =>
                              m.id === message.id
                                ? { ...m, content: newText }
                                : m
                            )
                          );
                          chatService
                            .saveChatHistory(
                              namespace,
                              chatId,
                              messages.map(m =>
                                m.id === message.id
                                  ? { ...m, content: newText }
                                  : m
                              )
                            )
                            .catch(() => {});
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
          {notice && (
            <div className={styles.messageGroup}>
              <div className={styles.aiMessage}>
                <div className={styles.errorBubble}>{notice}</div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area - Using Figma FieldPanel */}
      <div className={styles.inputArea}>
        <FieldPanel
          value={inputValue}
          onChange={setInputValue}
          onSend={handleSendMessage}
          onKeyDown={handleKeyDown}
          onToolsClick={(anchorRect?: DOMRect) => {
            if (anchorRect) {
              const viewportHeight = window.innerHeight;
              const bottom = Math.max(20, viewportHeight - anchorRect.top + 12);
              const right = Math.max(20, window.innerWidth - anchorRect.right);
              setSettingsAnchor({ bottom, right });
            } else {
              setSettingsAnchor({});
            }
            setIsSettingsOpen(true);
          }}
          disabled={isLoading}
        />
      </div>

      {/* Settings Bar - Slides in from right */}
      <SettingsBar
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        anchorRight={settingsAnchor.right}
        anchorBottom={settingsAnchor.bottom}
      />
    </div>
  );
};

export default TextChatPage;
